// Application Components
import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import { object, string } from 'yup'
import {
    dateEqualToTodayField,
    translateField,
} from '../../functions/field-functions'
import { extractSubmissionForm } from '../../functions/form-functions'

//// Application Slices
import { selectSettings } from '../../reducers/settingSlice'
import {
    updateFormData,
    updateFormValidity,
    updateFormSystem,
    selectForms,
} from '../../reducers/formSlice'

//// Application Components
import ButtonComponent from '../../components/conditional-components/button-component'
import LoadingComponent from '../../components/simple-components/loading-component'
import NotificationComponent from '../../components/conditional-components/notification-component'

//// Carbon Components
import {
    Form,
    RadioButtonGroup,
    RadioButton,
    DatePicker,
    DatePickerInput,
} from '@carbon/react'

//// Carbon Icons

//// Static Constants

//////// Component
const ElectronicFingerprintingForm = ({ instance }) => {
    //////// Hooks
    const dispatch = useDispatch()
    const navigate = useNavigate()

    //////// State
    const [loading, setLoading] = useState(false)
    const [notification, setNotification] = useState({})

    //////// Slices
    const settingsData = useSelector(selectSettings)
    const formsData = useSelector(selectForms)

    //// Slice Data
    const digital_name = settingsData?.['form']?.['digital_name']
    const submit_route = settingsData?.['form']?.['submit_route']
    const fields = settingsData?.['form']?.['fields']
    const ori_number = settingsData?.['form']?.['ori_number']

    //////// Search Params

    //////// Methods

    // Validate Data Submission
    const validation = async (submit) => {
        // Validate Form Submission Data Against Schema
        try {
            // Schema Object
            const schema = {}
            schema.yup = object({
                BKGRD_AFFR: string().required(
                    'This field is required to be entered or selected.'
                ),
                DTE_BKRDAFF: string()
                    .test('dte', `Must be Today's Date`, () =>
                        dateEqualToTodayField(
                            formsData,
                            digital_name,
                            'DTE_BKRDAFF'
                        )
                    )
                    .required(
                        'This field is required to be entered or selected.'
                    ),
            })
            schema.valid = {
                BKGRD_AFFR: false,
                BKGRD_AFFRErrMsg: '',
                DTE_BKRDAFF: false,
                DTE_BKRDAFFErrMsg: '',
            }
            await schema.yup.validate(
                { ...submit },
                { strict: true, abortEarly: false }
            )
            dispatch(
                updateFormValidity({
                    digital_name: digital_name,
                    errors: {},
                })
            )
            return true
        } catch (err) {
            const errors = {}
            if (err?.inner?.length > 0) {
                await err.inner.forEach(async (el) => {
                    if (el?.path) {
                        errors[el?.path] = true
                        if (el?.errors?.length > 0) {
                            const errName = el?.path + 'ErrMsg'
                            const errMsg = el?.errors?.join(' ')
                            const tran = translateField(fields, el?.path)
                            if (tran) {
                                const tranErrMsg = await errMsg.replaceAll(
                                    el?.path,
                                    tran
                                )
                                errors[errName] = tranErrMsg
                            } else {
                                errors[errName] = el?.errors?.join(' ')
                            }
                        }
                    }
                })
            }
            //// Start Watson Assistant Journey

            //// End Watson Assistant Journey
            dispatch(
                updateFormValidity({
                    digital_name: digital_name,
                    errors: errors,
                })
            )
            return false
        }
    }

    // Re-Validate Data Submission
    const revalidation = async (event_name) => {
        try {
            // Update Validation State
            if (digital_name && event_name) {
                dispatch(
                    updateFormValidity({
                        digital_name: digital_name,
                        errors: {
                            [event_name]: false,
                            [event_name + 'ErrMsg']: '',
                        },
                    })
                )
            }
        } catch (error) {
            console.warn('Revalidation Error:', error)
            return null
        }
    }

    //////// Functional Methods

    // Handle Data Of All Fields + DatePicker (ID)
    const handleData = (e, id) => {
        if (e?.target?.name && e?.target?.value) {
            dispatch(
                updateFormData({
                    digital_name: digital_name,
                    values: {
                        [e.target.name]: e.target.value,
                    },
                })
            )
            revalidation(e.target.name)
        } else if (e?.target?.name) {
            dispatch(
                updateFormData({
                    digital_name: digital_name,
                    values: { [e.target.name]: '' },
                })
            )
            revalidation(e.target.name)
        } else if (e?.length > 0 && id) {
            dispatch(
                updateFormData({
                    digital_name: digital_name,
                    values: {
                        [id]: format(new Date(e?.[0]), 'MM/dd/yyyy'),
                    },
                })
            )
            revalidation(id)
        }
    }

    // Handle Form Submission
    const handleSubmit = async () => {
        try {
            console.info('Electronic Fingerprinting: Submitted')

            setLoading(true)

            // Extract Form Data From Redux
            const submit = await extractSubmissionForm(digital_name, formsData)

            // Validate Form Submission Data
            const valid = await validation(submit)

            // Check If Form Data Is Valid
            if (valid) {
                console.info('Electronic Fingerprinting: Passed Valid')

                dispatch(
                    updateFormSystem({
                        digital_name: digital_name,
                        system: { valid: true },
                    })
                )
                setLoading(false)
                setNotification({
                    type: 'inline',
                    visible: false,
                    kind: '',
                    title: '',
                    subtitle: '',
                })

                // Navigate To Next Form
                navigate(submit_route ? submit_route : '/')
            } else {
                console.info('Electronic Fingerprinting: Failed Valid')

                dispatch(
                    updateFormSystem({
                        digital_name: digital_name,
                        system: { valid: false },
                    })
                )
                setLoading(false)
                setNotification({
                    type: 'inline',
                    visible: true,
                    kind: 'error',
                    title: 'Form Errors:',
                    subtitle:
                        'There are errors on the form (such as missing required info). Please scroll up and update the fields with red messages.',
                })

                console.log(
                    'Electronic Fingerprinting Failed Valid: There are errors on the form (such as missing required info). Please scroll up and update the fields with red messages.'
                )
            }
        } catch (error) {
            console.warn('Handle Submit Error:', error)
            return null
        }
    }

    // Handle Keyboard Enter As Submit (Method)
    const handleKeyDown = async (event) => {
        try {
            if (event?.key === 'Enter') {
                await handleSubmit()
            }
        } catch (error) {
            console.warn('Handle Down Key Error:', error)
            return null
        }
    }

    //////// Memoization

    //////// Effects/Side Effects

    // Reload Watson Assistant Journey on Reload
    useEffect(() => {
        if (instance?.tours) {
            instance.tours.endTour()
            instance.tours.startTour(
                'Registered Nursing Endorsement Electronic Fingerprinting Tour'
            )
        }
        return () => {}
    }, [instance])

    //////////////// Render & Return
    if (loading) {
        return (
            <>
                <Form
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    onSubmit={handleSubmit}
                >
                    <LoadingComponent />
                </Form>
            </>
        )
    } else {
        return (
            <>
                <Form
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    onSubmit={handleSubmit}
                >
                    <p>
                        Read text below and review the FBI Privacy statement.
                        Confirm you have completed this task and enter{' '}
                        <strong>{`TODAY's`}</strong> date in the section below.
                    </p>

                    <p>
                        <strong>FLORIDA DEPARTMENT OF LAW ENFORCEMENT:</strong>{' '}
                        NOTICE FOR APPLICANTS SUBMITTING FINGERPRINTS WHERE
                        CRIMINAL RECORD RESULTS WILL BECOME PART OF THE CARE
                        PROVIDER BACKGROUND SCREENING CLEARINGHOUSE.
                    </p>

                    <p>
                        <strong>NOTICE OF:</strong>
                        <ul>
                            <li>
                                <strong>
                                    SHARING OF CRIMINAL HISTORY RECORD
                                    INFORMATION WITH SPECIFIED AGENCIES,
                                </strong>
                            </li>
                            <li>
                                <strong>RETENTION OF FINGERPRINTS,</strong>,
                            </li>
                            <li>
                                <strong>PRIVACY POLICY</strong>
                            </li>
                            <li>
                                <strong>
                                    RIGHT TO CHALLENGE AN INCORRECT CRIMINAL
                                    HISTORY RECORD
                                </strong>
                            </li>
                        </ul>
                    </p>

                    <p>
                        This notice is to inform you that when you submit a set
                        of fingerprints to the Florida Department of Law
                        Enforcement (FDLE) for the purpose of conducting a
                        search for any Florida and national criminal history
                        records that may pertain to you, the results of that
                        search will be returned to the Care Provider Background
                        Screening Clearinghouse. By submitting fingerprints, you
                        are authorizing the dissemination of any state and
                        national criminal history record that may pertain to you
                        to the Specified Agency or Agencies from which you are
                        seeking approval to be employed, licensed, work under
                        contract, or to serve as a volunteer, pursuant to the
                        National Child Protection Act of 1993, as amended, and
                        section 943.0542, Florida Statutes. &quot;Specified
                        agency&quot; means the Department of Health, the
                        Department of Children and Family Services, the Division
                        of Vocational Rehabilitation within the Department of
                        Education, the Agency for Health Care Administration,
                        the Department of Elder Affairs, the Department of
                        Juvenile Justice, and the Agency for Persons with
                        Disabilities when these agencies are conducting state
                        and national criminal history background screening on
                        persons who provide care for children or persons who are
                        elderly or disabled. The fingerprints submitted will be
                        retained by FDLE and the Clearinghouse will be notified
                        if FDLE receives Florida arrest information on you.
                    </p>

                    <p>
                        Your Social Security Number (SSN) is needed to keep
                        records accurate because other people may have the same
                        name and birth date. Disclosure of your SSN is
                        imperative for the performance of the Clearinghouse
                        agencies duties in distinguishing your identity from
                        that of other persons whose identification information
                        may be the same as or similar to yours. Licensing and
                        employing agencies are allowed to release a copy of the
                        state and national criminal record information to a
                        person who requests a copy of his or her own record if
                        the identification of the record was based on submission
                        of the {`person's`} fingerprints. Therefore, if you wish
                        to review your record, you may request that the agency
                        that is screening the record provide you with a copy.
                        After you have reviewed the criminal history record, if
                        you believe it is incomplete or inaccurate, you may
                        conduct a personal review as provided in s. 943.056,
                        Florida Statutes, and Rule 11C8.001, F.A.C. If national
                        information is believed to be in error, the FBI should
                        be contacted at 304-625-2000. You can receive any
                        national criminal history record that may pertain to you
                        directly from the FBI, pursuant to 28 CFR Sections
                        16.30-16.34. You have the right to obtain a prompt
                        determination as to the validity of your challenge
                        before a final decision is made about your status as an
                        employee, volunteer, contractor, or subcontractor.
                    </p>

                    <p>
                        Until the criminal history background check is
                        completed, you may be denied unsupervised access to
                        children, the elderly, or persons with disabilities.
                    </p>

                    <p>
                        The {`FBI's`} Privacy Statement can be viewed{' '}
                        <a
                            href="https://www.fbi.gov/services/cjis/compact-council/privacy-act-statement"
                            target="_blank"
                            rel="noreferrer noopener"
                            aria-label="privacy-act-statement-link"
                            type="link"
                        >
                            here
                        </a>
                        .
                    </p>

                    <RadioButtonGroup
                        className={
                            formsData?.[digital_name]?.errors?.BKGRD_AFFR
                                ? 'required invalid'
                                : 'required'
                        }
                        defaultSelected={
                            formsData?.[digital_name]?.values?.BKGRD_AFFR
                        }
                        valueSelected={
                            formsData?.[digital_name]?.values?.BKGRD_AFFR
                        }
                        legendText={`I have been provided and read the statement from the Florida Department of Law Enforcement regarding the sharing, retention, privacy and right to challenge incorrect criminal history records and the "Privacy Statement" document from the Federal Bureau of Investigation.`}
                        name="BKGRD_AFFR"
                        invalid={formsData?.[digital_name]?.errors?.BKGRD_AFFR}
                        invalidText={
                            formsData?.[digital_name]?.errors?.BKGRD_AFFRErrMsg
                        }
                    >
                        <RadioButton
                            id="BKGRD_AFFRTrue"
                            name="BKGRD_AFFRTrue"
                            labelText="Yes"
                            value="Y"
                            type="radio"
                            onClick={handleData}
                        />
                    </RadioButtonGroup>

                    <DatePicker
                        dateFormat="m/d/Y"
                        datePickerType="single"
                        onChange={(e) => handleData(e, 'DTE_BKRDAFF')}
                        onClose={(e) => handleData(e, 'DTE_BKRDAFF')}
                        value={formsData?.[digital_name]?.values?.DTE_BKRDAFF}
                    >
                        <DatePickerInput
                            className="required"
                            labelText="Enter today's date:"
                            id="DTE_BKRDAFF"
                            name="DTE_BKRDAFF"
                            placeholder="MM/DD/YYYY"
                            type="text"
                            invalid={
                                formsData?.[digital_name]?.errors?.DTE_BKRDAFF
                            }
                            invalidText={
                                formsData?.[digital_name]?.errors
                                    ?.DTE_BKRDAFFErrMsg
                            }
                        />
                    </DatePicker>

                    <p>
                        <strong>
                            The board will not receive your Livescan results if
                            you do not confirm the above statement by checking{' '}
                            {`"Yes."`}
                        </strong>
                    </p>

                    <p>
                        <strong>
                            <u>Electronic Fingerprinting</u>: (Required for ALL
                            applicants)
                        </strong>
                    </p>

                    <p>
                        All applicants, including out-of-state applicants, are
                        required to submit their fingerprints electronically.
                        The Department of Health accepts electronic
                        fingerprinting offered by Livescan service providers
                        that are approved by the Florida Department of Law
                        Enforcement. For a list of approved vendors, visit our
                        website at:{' '}
                        <a
                            href="https://flhealthsource.gov/background-screening/"
                            target="_blank"
                            rel="noreferrer noopener"
                            aria-label="background-screening-link"
                            type="link"
                        >
                            https://flhealthsource.gov/background-screening/
                        </a>
                        .
                    </p>

                    <p>
                        Typically background results submitted by Livescan are
                        received by the board within 24-72 hours of being
                        processed. The {`board's`} ORI number is{' '}
                        <strong>{ori_number}</strong>. The board cannot accept
                        hard fingerprint cards or results. All results must be
                        submitted electronically by the Livescan service
                        provider.
                    </p>

                    <p>
                        Livescan screenings performed by a Florida Police or{' '}
                        {`Sheriff's`} Department require that you login to the
                        FDLE Civil Applicant Payment System (CAPS) at{' '}
                        <a
                            href="https://caps.fdle.state.fl.us/"
                            target="_blank"
                            rel="noreferrer noopener"
                            aria-label="caps-fdle-link"
                            type="link"
                        >
                            https://caps.fdle.state.fl.us/
                        </a>{' '}
                        and pay a fee before your results will be released to
                        our office.
                    </p>

                    <p>
                        The Florida Department of Health retains fingerprints on
                        any applicant in the Care Provider Clearinghouse. One of
                        the requirements for your Livescan to be retained in the
                        Care Provider Clearinghouse is a photograph must be
                        taken by the Livescan service provider at the time of
                        fingerprinting. Your background screening results will
                        be retained for five years. Licensees will be notified
                        when their retention date is approaching and will be
                        provided with instructions on how to retain their
                        fingerprints to avoid having to submit a new background
                        screening.
                    </p>

                    <p>
                        Applicants needing hard fingerprint cards can request
                        them via email at MQA.BackgroundScreen@flhealth.gov.
                        Request must include the current mailing address you
                        want the cards mailed to. To find Livescan providers who
                        offer hard fingerprint card processing go to{' '}
                        <a
                            href="https://www.flhealthsource.gov/bgs-providers"
                            target="_blank"
                            rel="noreferrer noopener"
                            aria-label="background-screening-providers-link"
                            type="link"
                        >
                            https://www.flhealthsource.gov/bgs-providers
                        </a>
                        . Click on the{' '}
                        <strong>{`"Out of State/International"`}</strong> link.
                    </p>

                    <NotificationComponent notification={notification} />

                    <div className="button-group">
                        <div className="button-group-item">
                            <ButtonComponent
                                click={handleSubmit}
                                title="Save Progress"
                            />
                        </div>
                    </div>
                </Form>
            </>
        )
    }
}

export default ElectronicFingerprintingForm
